/**
 * The two membership routes: begin a join or a leave, and report on a 3Halves leg.
 *
 *   POST …/loyalty-consent/membership          {txn, op}
 *   POST …/loyalty-consent/membership/advance  {txn, result, code, wrote}
 *
 * Shaped like ../consent/provider.js, and for its reasons: the ID token as a bearer (these routes
 * are ours, D-068), `cache: 'no-store'`, `credentials: 'omit'`, and a deadline over the whole call
 * credential lookup included (../deadline.js).
 *
 * **It never rejects, and it reads the body on EVERY status.** The server puts `membership` on its
 * errors too: a join whose Auth0 write was rate-limited answers 503 WITH `membership.state:
 * 'aborted'`, and that state is the one fact the caller needs. A client that dropped non-2xx
 * bodies -- the mistake ../withdrawal.js's history records -- would be left guessing.
 */

import { withDeadline } from '../deadline.js';

/** How long one membership call may take. Above the server's own worst case for a begin. */
export const MEMBERSHIP_TIMEOUT_MS = 20000;

/**
 * @param {Object}                options               Wiring.
 * @param {string}                options.beginUrl      Root-relative.
 * @param {string}                options.advanceUrl    Root-relative.
 * @param {() => Promise<string>} options.getCredential The fan's ID token, or ''.
 * @param {Function}              options.transport     A `fetch`-shaped function.
 * @param {number}                [options.timeoutMs]   Per-call deadline.
 * @param {Object}                [options.timers]      `{set, clear}`, for tests.
 * @return {{available: boolean, begin: Function, advance: Function}} The transport.
 */
export function createMembershipTransport( {
	beginUrl,
	advanceUrl,
	getCredential,
	transport,
	timeoutMs = MEMBERSHIP_TIMEOUT_MS,
	timers,
} ) {
	/**
	 * @param {string} url  Endpoint.
	 * @param {Object} body JSON body.
	 * @return {Promise<Object>} A result. Never rejects.
	 */
	function post( url, body ) {
		if ( ! url ) {
			return Promise.resolve( result( false, 0, 'unconfigured', null ) );
		}

		return withDeadline(
			signal =>
				Promise.resolve( typeof getCredential === 'function' ? getCredential() : '' ).then(
					credential => {
						if ( ! credential ) {
							return result( false, 0, 'no-credential', null );
						}

						return transport( url, {
							method: 'POST',
							cache: 'no-store',
							credentials: 'omit',
							signal,
							headers: {
								Authorization: `Bearer ${ credential }`,
								'Content-Type': 'application/json',
								Accept: 'application/json',
							},
							body: JSON.stringify( body ),
						} ).then( readResponse );
					}
				),
			timeoutMs,
			{ timers }
		).catch( error =>
			result( false, 0, error && error.timedOut ? 'timeout' : 'network', null )
		);
	}

	return {
		available: Boolean( beginUrl && advanceUrl ),

		/**
		 * @param {string} txn A browser-minted UUID: the idempotency key.
		 * @param {string} op  'join' or 'leave'.
		 * @return {Promise<Object>} A result.
		 */
		begin( txn, op ) {
			return post( beginUrl, { txn, op } );
		},

		/**
		 * @param {string} txn    The transaction.
		 * @param {Object} report `{result, code, wrote}`.
		 * @return {Promise<Object>} A result.
		 */
		advance( txn, report ) {
			return post( advanceUrl, {
				txn,
				result: report.result,
				code: String( report.code || '' ),
				wrote: Boolean( report.wrote ),
			} );
		},
	};
}

/**
 * @param {Object} response A fetch Response.
 * @return {Promise<Object>} A result.
 */
function readResponse( response ) {
	const status = Number( response.status ) || 0;

	return Promise.resolve()
		.then( () => ( typeof response.json === 'function' ? response.json() : null ) )
		.then(
			body => body,
			() => null
		)
		.then( body => {
			const ok = status >= 200 && status < 300;

			return result(
				ok,
				status,
				ok ? '' : ( body && typeof body.code === 'string' && body.code ) || 'http',
				body
			);
		} );
}

/**
 * @param {boolean}     ok     Whether the call succeeded.
 * @param {number}      status HTTP status, or 0.
 * @param {string}      reason '' on success; the server's code, or a transport cause.
 * @param {Object|null} body   The parsed body.
 * @return {{ok: boolean, status: number, reason: string, granted: (boolean|null), checkedAt: number, membership: (Object|null)}} The result.
 */
function result( ok, status, reason, body ) {
	const payload = body && typeof body === 'object' ? body : {};

	return {
		ok,
		status,
		reason,
		granted: typeof payload.granted === 'boolean' ? payload.granted : null,
		checkedAt: Number( payload.checkedAt ) || 0,
		membership:
			payload.membership && typeof payload.membership === 'object'
				? payload.membership
				: null,
	};
}
